/**
 * Décrire une figure v2 et une construction de l'atelier graphique — en mots
 * et en mesures, jamais par un modèle.
 *
 *  - `decrireFigureV2` : la phrase d'un lecteur d'écran pour une figure de
 *    défi. Elle vivait dans l'application mobile
 *    (`figureV2-affichage.ts`) ; elle est ici pour que l'API la lise aussi
 *    (doc 26, § 6.2). Même signature, même phrase.
 *  - `decrireConstruction` : ce que l'élève a construit, et comment — une
 *    phrase par objet, qui dit sa définition, et les mesures (doc 26, § 6.2).
 *    La même phrase sert à la liste des objets lue par VoiceOver (§ 2.1) et
 *    à la correction.
 *
 * Déterministe : déduit des DONNÉES normalisées ou résolues, jamais écrit
 * par un modèle — la même discipline que `decrireFigureResolue` pour le v1.
 */
import { decrireCaracteristiques, reduireEsquisse } from './esquisse.js';
import { estIdEnonce, texteMesure, } from './construction.js';
import { mesureAngle, } from './figure-v2.js';
import { distance, formaterNombre } from './geometrie.js';
/* ======================================================= la figure v2 */
const NOM_SOLIDE = {
    cube: 'Cube',
    prisme_droit: 'Prisme droit',
    pyramide_droite: 'Pyramide droite',
    cylindre: 'Cylindre',
    cone: 'Cône droit',
    boule: 'Boule',
};
const ADJECTIF_BASE = {
    triangle: 'triangulaire',
    carre: 'carrée',
    rectangle: 'rectangulaire',
    hexagone: 'hexagonale',
};
const NOM_FONCTION = {
    lineaire: 'linéaire',
    quadratique: 'quadratique',
    exponentielle: 'exponentielle',
    valeur_absolue: 'valeur absolue',
    racine: 'racine carrée',
    logarithmique: 'logarithmique',
    rationnelle: 'rationnelle',
    sinusoidale: 'sinusoïdale',
    tangente: 'tangente',
};
/** Au-delà, une liste de noms devient un compte : une phrase de trente points ne s'écoute pas. */
const NOMS_MAX = 12;
const nb = (v) => formaterNombre(v);
/** Le nom d'un solide, tel qu'un titre le dit : « Cône droit », « Prisme droit à base carrée ». */
export function nomSolideV2(s) {
    const nom = NOM_SOLIDE[s.famille];
    return s.famille === 'prisme_droit' || s.famille === 'pyramide_droite'
        ? `${nom} à base ${ADJECTIF_BASE[s.base]}`
        : nom;
}
function dimensionsSolide(s) {
    switch (s.famille) {
        case 'cube':
            return `arête ${nb(s.arete)}`;
        case 'prisme_droit':
        case 'pyramide_droite': {
            const base = s.profondeurBase === null
                ? `côté de la base ${nb(s.coteBase)}`
                : `base de ${nb(s.coteBase)} sur ${nb(s.profondeurBase)}`;
            return `${base}, hauteur ${nb(s.hauteur)}`;
        }
        case 'cylindre':
        case 'cone':
            return `rayon ${nb(s.rayon)}, hauteur ${nb(s.hauteur)}`;
        case 'boule':
            return `rayon ${nb(s.rayon)}`;
    }
}
/**
 * La phrase d'un lecteur d'écran, déduite des données normalisées. Elle dit
 * ce que l'élève VOIT — solide et ses cotes, points, polygones, cotes, angles,
 * étiquettes — et rien de ce que la figure demande de trouver (une cote
 * « a = ? » est dite telle quelle).
 */
export function decrireFigureV2(f) {
    if (f.espace === 'espace') {
        const cotes = f.cotes.length > 0 ? ` ; cotes : ${f.cotes.map((c) => c.texte).join(', ')}` : '';
        return `${nomSolideV2(f.solide)} : ${dimensionsSolide(f.solide)}${cotes}.`;
    }
    return `Figure dans le plan : ${partiesDuPlan(f).join(' ; ')}.`;
}
function partiesDuPlan(f) {
    const nomDe = new Map();
    const cle = (p) => `${p.x};${p.y}`;
    const de = (type) => f.objets.filter((o) => o.type === type);
    for (const p of de('point'))
        if (p.nom !== '')
            nomDe.set(cle(p), p.nom);
    const nomme = (p) => nomDe.get(cle(p)) ?? null;
    const suite = (ps) => {
        const noms = ps.map(nomme);
        return noms.every((n) => n !== null) ? noms.join('') : null;
    };
    const liste = (noms, seul, pluriel) => noms.length > NOMS_MAX
        ? `${noms.length} ${pluriel}`
        : `${noms.length > 1 ? pluriel : seul} ${noms.join(', ')}`;
    const parties = [];
    const r = f.repere;
    if (r.axes) {
        parties.push(`repère de ${nb(r.xMin)} à ${nb(r.xMax)} en x et de ${nb(r.yMin)} à ${nb(r.yMax)} en y`);
    }
    const points = de('point')
        .filter((p) => p.creux === undefined)
        .map((p) => p.nom);
    if (points.length > 0)
        parties.push(liste(points, 'point', 'points'));
    /* Les BORNES (tranche T42) : pleines ou vides, dites une à une, à leur place. */
    for (const p of de('point')) {
        if (p.creux === undefined)
            continue;
        parties.push(`point ${p.creux ? 'vide' : 'plein'}${p.nom === '' ? '' : ` ${p.nom}`} en (${nb(p.x)} ; ${nb(p.y)})`);
    }
    const polygones = de('polygone');
    if (polygones.length > 0) {
        const noms = polygones.map((p) => suite(p.sommets));
        parties.push(polygones.length <= 3 && noms.every((n) => n !== null)
            ? noms.map((n) => `polygone ${n}`).join(', ')
            : `${polygones.length} polygone${polygones.length > 1 ? 's' : ''}`);
    }
    const segments = f.objets.filter((o) => o.type === 'segment' || o.type === 'droite');
    if (segments.length > 0) {
        const noms = segments.map((s) => suite([s.de, s.a]));
        parties.push(segments.length <= 4 && noms.every((n) => n !== null)
            ? noms.map((n) => `segment ${n}`).join(', ')
            : `${segments.length} segment${segments.length > 1 ? 's' : ''}`);
    }
    /* La région d'une conique (tranche T38) : de quel côté, et si la courbe en fait partie. */
    const regionDe = (c) => c.region === undefined
        ? ''
        : `, région ${c.region === 'interieur' ? 'intérieure' : 'extérieure'} hachurée, ` +
            `frontière ${c.stricte === true ? 'exclue (en pointillé)' : 'comprise'}`;
    const lieu = (p) => nomme(p) ?? `(${nb(p.x)} ; ${nb(p.y)})`;
    /* Un vecteur NOMMÉ (tranche T43) se dit par son nom, « vecteur u », et ses bouts. */
    for (const v of de('vecteur')) {
        if (v.nom !== undefined)
            parties.push(`vecteur ${v.nom} de ${lieu(v.de)} à ${lieu(v.a)}`);
    }
    for (const c of de('cercle')) {
        const centre = nomme(c.centre);
        parties.push(`cercle${centre === null ? '' : ` de centre ${centre}`} et de rayon ${nb(c.rayon)}${regionDe(c)}`);
    }
    for (const e of de('ellipse')) {
        parties.push(`ellipse de centre ${lieu(e.centre)}, de demi-axe horizontal ${nb(e.a)} et de demi-axe vertical ${nb(e.b)}` +
            `${e.pointille === true ? ', en pointillé' : ''}${regionDe(e)}`);
    }
    for (const h of de('hyperbole')) {
        const sommets = h.ouverture === 'horizontale'
            ? `ouverte vers la gauche et vers la droite, sommets à ${nb(h.a)} du centre`
            : `ouverte vers le haut et vers le bas, sommets à ${nb(h.b)} du centre`;
        parties.push(`hyperbole de centre ${lieu(h.centre)}, ${sommets}` +
            `${h.asymptotes === true ? ', ses deux asymptotes en pointillé' : ''}` +
            `${h.rectangle === true ? `, son rectangle de ${nb(2 * h.a)} sur ${nb(2 * h.b)} en pointillé` : ''}` +
            `${h.pointille === true ? ', en pointillé' : ''}${regionDe(h)}`);
    }
    for (const pb of de('parabole')) {
        const vers = pb.axe === 'horizontal'
            ? pb.c > 0
                ? 'la droite'
                : 'la gauche'
            : pb.c > 0
                ? 'le haut'
                : 'le bas';
        parties.push(`parabole de sommet ${lieu(pb.sommet)}, ouverte vers ${vers}` +
            `${pb.foyer === true ? ', son foyer marqué' : ''}` +
            `${pb.directrice === true ? ', sa directrice en pointillé' : ''}` +
            `${pb.pointille === true ? ', en pointillé' : ''}${regionDe(pb)}`);
    }
    for (const fn of de('fonction')) {
        parties.push(`courbe d'une fonction ${NOM_FONCTION[fn.fonction.famille]}${fn.pointille === true ? ', en pointillé' : ''}`);
    }
    /* La fonction définie par parties (tranche T42) : chaque partie, son intervalle, ses bornes. */
    const BORNE = { plein: 'fermée', vide: 'ouverte' };
    for (const pp of de('par_parties')) {
        const morceaux = pp.parties.map((p) => {
            const [a, b] = p.domaine;
            const bornes = p.bouts
                .map((bout, i) => bout === 'aucun' ? null : `borne ${BORNE[bout]} en x = ${nb(i === 0 ? a : b)}`)
                .filter((t) => t !== null);
            return (`${NOM_FONCTION[p.fonction.famille]} de x = ${nb(a)} à x = ${nb(b)}` +
                (bornes.length > 0 ? `, ${bornes.join(', ')}` : ''));
        });
        parties.push(`fonction définie par parties, en ${pp.parties.length} parties${pp.pointille === true ? ', en pointillé' : ''} : ` +
            morceaux.join(' ; '));
    }
    for (const rg of de('region')) {
        parties.push(`région hachurée ${rg.cote === 'dessus' ? 'au-dessus' : 'au-dessous'} de la courbe d'une fonction ` +
            `${NOM_FONCTION[rg.fonction.famille]}, frontière ${rg.stricte ? 'exclue (en pointillé)' : 'comprise'}`);
    }
    const cotes = de('cote').map((c) => c.texte);
    if (cotes.length > 0)
        parties.push(`cotes : ${cotes.join(', ')}`);
    for (const a of de('angle')) {
        const sommet = nomme(a.sommet);
        const ou = sommet === null ? '' : ` en ${sommet}`;
        if (a.droit)
            parties.push(`angle droit${ou}`);
        else if (a.texte !== null)
            parties.push(`angle${ou} marqué ${a.texte}`);
        else
            parties.push(`angle marqué${ou}`);
    }
    const etiquettes = de('etiquette').map((e) => e.texte);
    if (etiquettes.length > 0)
        parties.push(liste(etiquettes, 'étiquette', 'étiquettes'));
    return parties.length > 0 ? parties : ['vide'];
}
/** Un nom de point simple (« A », « A′ », « B1 ») s'écrit collé aux autres : [AB], ABC. */
const NOM_SIMPLE = /^\p{Lu}[′'’\d]*$/u;
/** Au-delà, les côtés d'un polygone ne sont pas détaillés dans les mesures. */
const COTES_MAX = 12;
/**
 * Les définitions dont la phrase porte DÉJÀ le nom de l'objet : un point
 * construit s'annonce « Point M, milieu de [AB] », une courbe « f(x) = … ».
 * Leur ajouter « , nommé M » le dirait deux fois.
 */
const NOM_DEJA_DIT = new Set([
    'point_libre',
    'point_sur',
    'milieu',
    'intersection',
    'fonction',
    'fonction_par_points',
]);
const GENRE_NOM = {
    droite: 'f',
    mesure_longueur: 'f',
    mesure_angle: 'f',
    demi_droite: 'f',
    perpendiculaire: 'f',
    parallele: 'f',
    mediatrice: 'f',
    bissectrice: 'f',
    translation: 'f',
    rotation: 'f',
    reflexion: 'f',
    homothetie: 'f',
    image: 'f',
    fonction: 'f',
    fonction_par_points: 'f',
    table_valeurs: 'f',
    esquisse: 'f',
};
/** Comment se dit le côté d'un demi-plan, dans une phrase. */
const MOT_COTE = {
    dessus: 'au-dessus de',
    dessous: 'au-dessous de',
    gauche: 'à gauche de',
    droite: 'à droite de',
};
/**
 * La règle d'une fonction, exposée (chantier « Élève », lot 8, L0) : les
 * contraintes d'un tracé se disent en phrases (`contraintes-en-phrases.ts`), et
 * « la courbe de y = 2x + 1 » s'écrit comme la description de la figure.
 */
export function regleFonctionV2(f) {
    return regleFonction(f);
}
/**
 * La règle d'une fonction, écrite comme l'élève l'écrit : « −0,5x + 3 ».
 * Déduite de la forme canonique, jamais d'un texte reçu — c'est la même
 * source que la courbe dessinée, donc la phrase ne peut pas la contredire.
 */
function regleFonction(f) {
    const nb = (v) => formaterNombre(v);
    /* Une règle s'écrit avec le signe moins typographique, comme les termes qui
       suivent : « −2|x| + 4 », jamais « -2|x| + 4 ». Deux signes moins dans la
       même ligne se verraient. */
    const signe = (v) => nb(v).replace(/^-/u, '−');
    /** Un terme qui SUIT un autre : son signe s'écrit à la place du sien. */
    const suivant = (v, apres = '') => 
    /* Un terme nul ne s'écrit pas : « −x² + 4 », jamais « −x² + 0x + 4 ». */
    v === 0 ? '' : ` ${v < 0 ? '−' : '+'} ${nb(Math.abs(v))}${apres}`;
    /** Le facteur d'un x : « x », « −x », « 2x ». */
    const facteur = (v, quoi) => v === 1 ? quoi : v === -1 ? `−${quoi}` : `${signe(v)}${quoi}`;
    /** Le contenu d'une valeur absolue ou d'une racine : b(x − h). */
    const dedans = (b, h) => {
        const x = h === 0 ? 'x' : `(x ${h < 0 ? '+' : '−'} ${nb(Math.abs(h))})`;
        return facteur(b, x);
    };
    switch (f.famille) {
        case 'lineaire':
            return `${facteur(f.a, 'x')}${suivant(f.b)}`;
        case 'quadratique':
            return `${facteur(f.a, 'x²')}${suivant(f.b, 'x')}${suivant(f.c)}`;
        case 'exponentielle':
            return `${facteur(f.a, `${nb(f.b)}^x`)}${suivant(f.c)}`;
        case 'valeur_absolue':
            return `${facteur(f.a, `|${dedans(f.b, f.h)}|`)}${suivant(f.k)}`;
        case 'racine':
            return `${facteur(f.a, `√(${dedans(f.b, f.h)})`)}${suivant(f.k)}`;
        case 'logarithmique':
            return `${facteur(f.a, `log en base ${nb(f.c)} de ${dedans(f.b, f.h)}`)}${suivant(f.k)}`;
        case 'rationnelle': {
            /* « 2 sur (x − 1) + 3 » : le dénominateur se dit entre parenthèses dès qu'il n'est pas « x » seul. */
            const bas = dedans(f.b, f.h);
            return `${signe(f.a)} sur ${bas === 'x' ? 'x' : `(${bas})`}${suivant(f.k)}`;
        }
        case 'sinusoidale': {
            /* « 2 sin(3(x − π/3)) + 1 » : b et h s'écrivent en π quand ils en sont un multiple simple. */
            const pi = (v) => enPiTexte(v) ?? nb(v);
            const h = f.h === 0 ? 'x' : `(x ${f.h < 0 ? '+' : '−'} ${pi(Math.abs(f.h))})`;
            const b = pi(f.b).replace(/^-/u, '−');
            const arg = f.b === 1 ? h : f.b === -1 ? `−${h}` : b.includes('/') ? `(${b})${h}` : `${b}${h}`;
            return `${facteur(f.a, `${f.fonction}(${arg})`)}${suivant(f.k)}`;
        }
        case 'tangente': {
            const pi = (v) => enPiTexte(v) ?? nb(v);
            const h = f.h === 0 ? 'x' : `(x ${f.h < 0 ? '+' : '−'} ${pi(Math.abs(f.h))})`;
            const b = pi(f.b).replace(/^-/u, '−');
            const arg = f.b === 1 ? h : f.b === -1 ? `−${h}` : b.includes('/') ? `(${b})${h}` : `${b}${h}`;
            return `${facteur(f.a, `tan(${arg})`)}${suivant(f.k)}`;
        }
    }
}
/** Un multiple simple de π (k·π/n, n ≤ 12), écrit « π/3 », « 2π », « −π/4 » ; `null` sinon. */
function enPiTexte(v) {
    if (v === 0)
        return null;
    for (let n = 1; n <= 12; n += 1) {
        const k = Math.round((v * n) / Math.PI);
        if (k !== 0 && Math.abs((k * Math.PI) / n - v) < 1e-9) {
            const a = Math.abs(k);
            return `${k < 0 ? '−' : ''}${a === 1 ? '' : a}π${n === 1 ? '' : `/${n}`}`;
        }
    }
    return null;
}
/**
 * Phrases et mesures : ce que l'élève a construit, et comment. Déterministe.
 *
 * `resolue` est `resoudreConstruction(doc, enonce)` : les noms des points de
 * l'énoncé et les valeurs calculées en viennent.
 */
export function decrireConstruction(doc, resolue) {
    const unite = doc.unite;
    const longueur = (v) => texteMesure('longueur', v, unite);
    const angle = (v) => texteMesure('angle', v, unite);
    const coord = (p) => `(${nb(p.x)} ; ${nb(p.y)})`;
    const nomDe = (id) => {
        const o = resolue.objets.get(id);
        if (o?.nom !== undefined)
            return o.nom;
        if (estIdEnonce(id)) {
            const rang = Number(id.slice(2));
            return Number.isInteger(rang) ? `objet ${rang + 1} de l'énoncé` : id.slice(2);
        }
        return id;
    };
    /** Le nom d'un point de l'énoncé posé là ; `null` : aucun. */
    const nomEn = (p) => {
        for (const q of resolue.objets.values()) {
            if (q.genre === 'point' &&
                estIdEnonce(q.id) &&
                q.nom !== undefined &&
                distance(q.p, p) < 1e-6) {
                return q.nom;
            }
        }
        return null;
    };
    /**
     * L3 (F3) — un objet SANS NOM désigné par ce qu'il est : « le triangle ABC »,
     * « [AB] », « l'arc de centre A », « la rotation de centre O ». Un objet
     * nommé garde son nom ; ce qu'on ne sait pas dire garde la désignation d'avant.
     */
    const designe = (id) => {
        const o = resolue.objets.get(id);
        if (o?.nom !== undefined)
            return o.nom;
        if (estIdEnonce(id) && o !== undefined) {
            if (o.genre === 'polygone') {
                const noms = o.sommets.map(nomEn);
                if (noms.every((n) => n !== null)) {
                    return `${o.sommets.length === 3 ? 'le triangle' : 'le polygone'} ${noms.join('')}`;
                }
            }
            if (o.genre === 'ligne') {
                const a = nomEn(o.a);
                const b = nomEn(o.b);
                if (a !== null && b !== null)
                    return o.sorte === 'segment' ? `[${a}${b}]` : `(${a}${b})`;
            }
        }
        const def = doc.objets.find((x) => x.id === id)?.def;
        switch (def?.type) {
            case 'cercle_report':
                return `l'arc de centre ${nomDe(def.centre)}`;
            case 'rotation':
                return `la rotation de centre ${nomDe(def.centre)}`;
            case 'homothetie':
                return `l'homothétie de centre ${nomDe(def.centre)}`;
            case 'reflexion':
                return `la réflexion d'axe ${designe(def.axe)}`;
            case 'translation':
                return `la translation de vecteur ${suite([def.de, def.a])}`;
            default:
                return nomDe(id);
        }
    };
    const suite = (ids) => {
        const noms = ids.map(nomDe);
        return noms.every((n) => NOM_SIMPLE.test(n)) ? noms.join('') : noms.join(', ');
    };
    const point = (id) => {
        const o = resolue.objets.get(id);
        return o?.genre === 'point' ? o.p : null;
    };
    const phrases = [];
    const mesures = [];
    const vues = new Set();
    const mesurer = (m) => {
        if (vues.has(m))
            return;
        vues.add(m);
        mesures.push(m);
    };
    /** [AM] et [MA] sont le même segment : une seule mesure. */
    const paires = new Set();
    const cote = (de, a) => {
        const p = point(de);
        const q = point(a);
        const cle = [de, a].sort().join('|');
        if (p === null || q === null || paires.has(cle))
            return;
        paires.add(cle);
        mesurer(`${suite([de, a])} = ${longueur(distance(p, q))}`);
    };
    for (const o of doc.objets) {
        const r = resolue.objets.get(o.id);
        const indefini = r === undefined || r.genre === 'indefini';
        phrases.push({
            id: o.id,
            texte: phraseDe(o, indefini ? (r?.genre === 'indefini' ? r.raison : 'non résolu') : null),
        });
        if (indefini)
            continue;
        const def = o.def;
        switch (def.type) {
            case 'point_libre': {
                const p = point(o.id);
                if (p !== null && resolue.repere.axes)
                    mesurer(`${nomDe(o.id)}${coord(p)}`);
                break;
            }
            case 'segment':
            case 'mesure_longueur':
                cote(def.de, def.a);
                break;
            case 'polygone':
                if (def.sommets.length <= COTES_MAX) {
                    def.sommets.forEach((s, i) => cote(s, def.sommets[(i + 1) % def.sommets.length]));
                }
                break;
            case 'cercle_par_point':
            case 'cercle_rayon':
                if (r.genre === 'cercle')
                    mesurer(`rayon du cercle de centre ${nomDe(def.centre)} = ${longueur(r.rayon)}`);
                break;
            case 'angle':
            case 'mesure_angle': {
                const s = point(def.sommet);
                const p = point(def.de);
                const q = point(def.a);
                if (s !== null && p !== null && q !== null) {
                    mesurer(`angle ${suite([def.de, def.sommet, def.a])} = ${angle(mesureAngle(s, p, q))}`);
                }
                break;
            }
            /* Le repère (phase 3) : ce qui se lit sur la figure et que le correcteur
               aurait sinon à recalculer — les composantes d'un vecteur, la règle
               d'une courbe. Une pente se lit dans la règle : on ne la répète pas. */
            case 'vecteur':
                if (r.genre === 'vecteur') {
                    mesurer(`vecteur ${suite([def.de, def.a])} = (${nb(r.a.x - r.de.x)} ; ${nb(r.a.y - r.de.y)})`);
                }
                break;
            case 'fonction':
            case 'fonction_par_points':
                if (r.genre === 'fonction')
                    mesurer(`${nomDe(o.id)}(x) = ${regleFonction(r.f)}`);
                break;
            default:
                break;
        }
    }
    function phraseDe(o, raison) {
        const def = o.def;
        const genre = GENRE_NOM[def.type] ?? 'm';
        const r = resolue.objets.get(o.id);
        let t;
        switch (def.type) {
            case 'point_libre':
                t = `Point ${nomDe(o.id)}, placé en ${coord(def)}`;
                break;
            case 'segment':
                t = `Segment [${suite([def.de, def.a])}]`;
                break;
            case 'droite':
                t = `Droite (${suite([def.de, def.a])})`;
                break;
            case 'cercle_par_point':
                t = `Cercle de centre ${nomDe(def.centre)} passant par ${nomDe(def.par)}`;
                break;
            case 'cercle_rayon':
                t = `Cercle de centre ${nomDe(def.centre)} et de rayon ${longueur(def.rayon)}`;
                break;
            case 'polygone': {
                const n = def.sommets.length;
                const quoi = n === 3 ? 'Triangle' : n === 4 ? 'Quadrilatère' : `Polygone à ${n} sommets`;
                t = `${quoi} ${suite(def.sommets)}`;
                break;
            }
            case 'angle':
                t = `Angle ${def.droit === true ? 'droit ' : ''}${suite([def.de, def.sommet, def.a])} marqué`;
                break;
            case 'mesure_longueur':
                t = `Mesure de la longueur ${suite([def.de, def.a])}`;
                if (r?.genre === 'mesure')
                    t += ` : ${longueur(r.valeur)}`;
                break;
            case 'mesure_angle':
                t = `Mesure de l'angle ${suite([def.de, def.sommet, def.a])}`;
                if (r?.genre === 'mesure')
                    t += ` : ${angle(r.valeur)}`;
                break;
            case 'texte':
                t = `Texte « ${def.texte} » en ${coord(def)}`;
                break;
            /* ── P2 : ce que la phase 2 a rendu vérifiable, et que la correction
               doit pouvoir LIRE. Sans ces phrases, une médiatrice juste arrivait au
               correcteur sous la forme « réservé à une phase ultérieure ». ── */
            case 'point_sur':
                t = `Point ${nomDe(o.id)}, sur ${nomDe(def.objet)}`;
                break;
            case 'milieu':
                t = `Point ${nomDe(o.id)}, milieu de [${suite([def.de, def.a])}]`;
                break;
            case 'intersection':
                t = `Point ${nomDe(o.id)}, intersection de ${designe(def.de)} et de ${designe(def.a)}`;
                break;
            case 'demi_droite':
                t = `Demi-droite d'origine ${nomDe(def.origine)} passant par ${nomDe(def.par)}`;
                break;
            case 'perpendiculaire':
                t = `Perpendiculaire à ${nomDe(def.a)} passant par ${nomDe(def.par)}`;
                break;
            case 'parallele':
                t = `Parallèle à ${nomDe(def.a)} passant par ${nomDe(def.par)}`;
                break;
            case 'mediatrice':
                t = `Médiatrice de [${suite([def.de, def.a])}]`;
                break;
            case 'bissectrice':
                t = `Bissectrice de l'angle ${suite([def.de, def.sommet, def.a])}`;
                break;
            case 'cercle_report':
                /* L3 (F3) : le geste du compas — l'ouverture reportée, dite par sa longueur. */
                t = `Arc de compas de centre ${nomDe(def.centre)}`;
                if (r?.genre === 'cercle')
                    t += `, de rayon ${longueur(r.rayon)}`;
                break;
            case 'translation':
                t = `Translation du vecteur ${suite([def.de, def.a])}`;
                break;
            case 'rotation':
                t = `Rotation de centre ${nomDe(def.centre)} et d'angle ${angle(def.angle)}`;
                break;
            case 'reflexion':
                t = `Réflexion d'axe ${designe(def.axe)}`;
                break;
            case 'homothetie':
                t = `Homothétie de centre ${nomDe(def.centre)} et de rapport ${nb(def.rapport)}`;
                break;
            case 'image':
                t = `Image de ${designe(def.source)} par ${designe(def.par)}`;
                break;
            /* ── P3 : le repère et les fonctions ── */
            case 'fonction':
            case 'fonction_par_points': {
                const par = def.type === 'fonction_par_points'
                    ? `, tracée par ${suite([def.points[0], def.points[1]])}`
                    : '';
                const regle = r?.genre === 'fonction' ? ` : ${nomDe(o.id)}(x) = ${regleFonction(r.f)}` : '';
                const sur = r?.genre === 'fonction' && r.domaine !== null
                    ? `, sur [${nb(r.domaine[0])} ; ${nb(r.domaine[1])}]`
                    : '';
                /* Tranche T42 : les bouts marqués d'une partie — borne fermée, borne ouverte. */
                const bornes = r?.genre === 'fonction' && r.domaine !== null && r.bouts !== undefined
                    ? r.bouts
                        .map((bout, i) => bout === 'aucun'
                        ? null
                        : `borne ${bout === 'plein' ? 'fermée' : 'ouverte'} en x = ${nb(r.domaine?.[i] ?? 0)}`)
                        .filter((x) => x !== null)
                    : [];
                const famille = def.type === 'fonction' ? NOM_FONCTION[def.famille] : 'linéaire';
                t = `Courbe d'une fonction ${famille}${par}${regle}${sur}${bornes.length > 0 ? `, ${bornes.join(', ')}` : ''}`;
                break;
            }
            case 'vecteur':
                t = `Vecteur ${suite([def.de, def.a])}`;
                if (r?.genre === 'vecteur') {
                    t += ` de composantes (${nb(r.a.x - r.de.x)} ; ${nb(r.a.y - r.de.y)})`;
                }
                break;
            case 'demi_plan':
                t =
                    `Demi-plan ${MOT_COTE[def.cote]} ${nomDe(def.frontiere)}, ` +
                        `frontière ${def.stricte ? 'exclue (en pointillé)' : 'comprise'}`;
                break;
            case 'table_valeurs': {
                const xs = def.x.map((x) => nb(x)).join(' ; ');
                t = `Table de valeurs de ${nomDe(def.fonction)} pour x = ${xs}`;
                break;
            }
            case 'esquisse': {
                /* L6 (F8) : une esquisse se dit par ce que le code en LIT, jamais par ses points. */
                const lue = reduireEsquisse(def.traits, resolue.repere);
                t = lue.lue
                    ? `Courbe esquissée${decrireCaracteristiques(lue.caracteristiques, resolue.repere).replace(/^une courbe/u, '')}`
                    : 'Courbe esquissée, illisible';
                break;
            }
        }
        const e = genre === 'f' ? 'e' : '';
        if (o.nom !== undefined && !NOM_DEJA_DIT.has(def.type))
            t += `, nommé${e} ${o.nom}`;
        if (o.style === 'pointille')
            t += ', en pointillé';
        if (o.copieDe !== undefined)
            t += `, recopié${e} de l'énoncé`;
        if (o.masque === true)
            t += `, masqué${e}`;
        if (raison !== null)
            t += `, sans solution pour l'instant (${raison})`;
        return t;
    }
    return {
        description: phrases.length === 0
            ? 'Aucun objet construit.'
            : `${phrases.map((p) => p.texte).join('. ')}.`,
        mesures,
        phrases,
    };
}
//# sourceMappingURL=decrire-v2.js.map